import { useEffect, useRef, useState } from 'react';

interface UseSmoothTimeProps {
    currentTime: number;
    isPlaying: boolean;
    isDragging: boolean;
    playbackSpeed?: number;
}

export function useSmoothTime({
    currentTime,
    isPlaying,
    isDragging,
    playbackSpeed = 1.0
}: UseSmoothTimeProps) {
    const [animatedCurrentTime, setAnimatedCurrentTime] = useState(currentTime);
    const animationFrameRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isPlaying || isDragging) {
            setAnimatedCurrentTime(currentTime);
            return;
        }

        const startTime = Date.now();
        const startVideoTime = currentTime;

        const animate = () => {
            if (!isPlaying || isDragging) {
                return;
            }

            const now = Date.now();
            const elapsedRealTime = (now - startTime) / 1000;
            const animatedTime = startVideoTime + (elapsedRealTime * playbackSpeed);

            setAnimatedCurrentTime(animatedTime);
            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [isPlaying, isDragging, currentTime, playbackSpeed]);

    return animatedCurrentTime;
}
